import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Wrench, BadgeCheck, HeartHandshake } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CHAPTERS, CONTACT, IMAGES } from "@/data/site";

const VALUES = [
  {
    icon: Wrench,
    title: "Factory-Safe Fitting",
    text: "Harness-coupled wiring and non-invasive mounts. Your manufacturer's warranty stays intact.",
  },
  {
    icon: BadgeCheck,
    title: "Genuine Parts Only",
    text: "Branded components with proper warranty bills. No grey-market substitutes, ever.",
  },
  {
    icon: HeartHandshake,
    title: "Honest Advice",
    text: "We recommend what your car needs — not what pads our invoice. That is why Palani trusts us.",
  },
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-16">
      <Seo
        title="About Us — Bowegans Car Care, Palani"
        siteName="Bowegans Car Care"
        description="Bowegans Car Care is Palani's aftermarket hub on NH.83 Bypass Road — master technicians, genuine parts, zero-compromise craftsmanship."
      />
      <section className="relative overflow-hidden border-b border-white/10">
        <img
          src={IMAGES.handwash}
          alt="Detailing work at Bowegans Car Care"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/70 to-[#0B0C0E]/40" />
        <div className="relative mx-auto max-w-7xl px-4 py-28 sm:px-6">
          <SectionHeading
            testid="about-page-heading"
            eyebrow="About us · Founded 2015 by Mr. G. Saravanabava, M.D"
            title="The garage Palani drives to."
            description="Bowegans Car Care was founded on a simple refusal: a car should never leave our bay looking ordinary."
          />
        </div>
      </section>
      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="about-chapters-section">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <SectionHeading
              testid="about-manifesto-heading"
              eyebrow="Our story"
              title="Three chapters. One obsession."
            />
            <Reveal delay={0.2} className="mt-8">
              <div className="relative overflow-hidden border border-[#262A35]" data-testid="about-founder-card">
                <img
                  src="/founder.jpg"
                  alt="G. Saravanabava, Founder and Managing Director of Bowegans Car Care"
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#3B82F6]">
                    Founder & Managing Director · Since 2015
                  </p>
                  <p className="font-display mt-1 text-2xl font-bold text-white" data-testid="about-founder-name">
                    G. Saravanabava
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="flex flex-col justify-center gap-10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.number} delay={i * 0.1}>
                <div className="flex gap-6 border-b border-white/10 pb-10 last:border-0 last:pb-0" data-testid={`about-chapter-${c.number}`}>
                  <span className="font-display text-stroke text-5xl font-black leading-none sm:text-6xl">
                    {c.number}
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-400 sm:text-base">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0E1013]" data-testid="about-values-section">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
          <SectionHeading
            testid="about-values-heading"
            eyebrow="How we work"
            title="Principles in the bay"
          />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1} className="h-full">
                <div className="spotlight-card h-full border border-[#262A35] bg-[#121418] p-7" data-testid={`about-value-${i}`}>
                  <v.icon className="h-6 w-6 text-[#3B82F6]" strokeWidth={1.8} />
                  <h3 className="font-display mt-4 text-lg font-bold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="about-visit-section">
        <Reveal>
          <div className="grid gap-8 border border-[#262A35] bg-[#121418] p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#3B82F6]">Visit the garage</p>
              <h2 className="font-display mt-3 text-2xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                On NH.83. Hard to miss.
              </h2>
              <div className="mt-6 flex items-start gap-3 text-sm text-gray-300" data-testid="about-address">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#3B82F6]" />
                {CONTACT.address}
              </div>
              <p className="font-mono2 mt-3 text-xs uppercase tracking-[0.25em] text-gray-500">
                {CONTACT.hours}
              </p>
            </div>
            <div className="flex flex-col items-start justify-center gap-4 lg:items-end">
              <a
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-directions-button"
                className="font-mono2 border border-white/20 px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:border-[#3B82F6]"
              >
                Get Directions
              </a>
              <Link
                to="/contact"
                data-testid="about-book-button"
                className="font-mono2 group inline-flex items-center gap-2 bg-[#3B82F6] px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#2563EB]"
              >
                Book a Visit
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
